import { ActionButton, CompoundButton, Icon, mergeStyles, Stack } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { DocumentOnePageSparkleRegular } from "@fluentui/react-icons";
import { logEvent } from "../../helper/AppInsightsHelper";
import CustomPanel from "../../SharedComponents/CustomPanel/CustomPanel";
import { useLazyGetSupportCaseAISummaryQuery } from "../../slices/ExploreCustomers.slice";
import { STYLES } from "./SupportTableAIInsightsPanel.styles";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";

export function SupportTableAIInsightsPanel({
  caseNumber,
  serviceRequestNumber
}: Readonly<{ caseNumber: number; serviceRequestNumber: number }>) {
  // Properties
  const [enablePanel, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [getAISummary, { data, isLoading, error }] = useLazyGetSupportCaseAISummaryQuery();

  const customProps = {
    caseNumber: caseNumber,
    serviceRequestNumber: serviceRequestNumber
  };

  // Events
  const handleDisplayAISummary = () => {
    openPanel();
    getAISummary(serviceRequestNumber, true);
    logEvent("Display AI Summary", "Customer Page", "Support Table AI Insights", customProps);
  };

  const renderCommandButtonItems = () => {
    if (error) {
      return <DisplayAPIsException error={error} height={250} />;
    }
    if (!data?.caseNumber) {
      return SUMMARY_NOT_AVAILABLE;
    }
    return commandButtonItems.map(({ key, text }) => (
      <Stack key={key} styles={STYLES.aiInsightsPanelContent}>
        <CompoundButton styles={STYLES.commandButton}>
          {renderLabelWithDescription({ key, text, description: (data as any)[key] })}
        </CompoundButton>
      </Stack>
    ));
  };

  return (
    <Stack styles={{ root: { height: "100%" } }}>
      <ActionButton
        ariaLabel="Case Summary"
        iconProps={{ iconName: "CRMCustomerInsightsApp" }}
        styles={STYLES.actionButton}
        onClick={handleDisplayAISummary}
        disabled={!caseNumber}
        title={!caseNumber ? "No summary available." : "Click here to see the AI-generated summary."}
      />
      <CustomPanel
        dismissPanel={dismissPanel}
        enablePanel={enablePanel}
        isLoading={isLoading}
        panelTitle="AI Case Summary"
      >
        {renderCommandButtonItems()}
      </CustomPanel>
    </Stack>
  );
}

const commandButtonItems: { key: string; text: string }[] = [
  {
    key: "overallSentiment",
    text: "Customer Sentiment"
  },
  {
    key: "caseTitle",
    text: "Case Title"
  },
  {
    key: "caseSummary",
    text: "Case Summary"
  },
  {
    key: "processImprovement",
    text: "Process Improvement"
  },
  {
    key: "productImprovement",
    text: "Product Improvement"
  },
  {
    key: "documentationImprovement",
    text: "Documentation Improvement"
  },
  {
    key: "toolImprovement",
    text: "Tool Improvement"
  },
  {
    key: "derivedProductName",
    text: "Derived Product Name"
  },
  {
    key: "surveyRating",
    text: "Survey Rating"
  }
];

const SUMMARY_NOT_AVAILABLE = (
  <div className={STYLES.textContainerDescription}>No case summary available for this case.</div>
);

// Functions
function renderLabelWithDescription({ key, text, description }: { key: string; text: string; description: string }) {
  let color = "";
  let iconName = "";

  if (key === "overallSentiment") {
    switch (description) {
      case "POSITIVE": {
        color = "#107c10";
        iconName = "Completed";
        break;
      }
      case "NEGATIVE": {
        color = "#a80000";
        iconName = "ErrorBadge";
        break;
      }
      default: {
        color = "#797673";
        iconName = "Blocked2";
        break;
      }
    }
    return (
      <span className={STYLES.textContainer}>
        <span className={mergeStyles(STYLES.textContainerLabel, { marginBottom: 0 })}>
          <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { justifyContent: "space-between" } }}>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <DocumentOnePageSparkleRegular style={STYLES.textContainerLabelIcon} />
              <span>{text}</span>
            </Stack>
            <Stack
              horizontal
              styles={{
                root: {
                  ...STYLES.textContainerDescriptionContent.root,
                  border: `2px solid ${color}`,
                  color,
                  padding: "0 1em"
                }
              }}
            >
              <Icon iconName={iconName} styles={STYLES.textContainerDescriptionContentIcon} />
              <div>{description}</div>
            </Stack>
          </Stack>
        </span>
      </span>
    );
  }

  return (
    <span className={STYLES.textContainer}>
      <span className={STYLES.textContainerLabel}>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DocumentOnePageSparkleRegular style={STYLES.textContainerLabelIcon} />
          <span>{text}</span>
        </Stack>
      </span>
      <div className={STYLES.textContainerDescription}>{!description ? "N/A" : description}</div>
    </span>
  );
}
